import { getCreatorApiUrl } from '@client/components/api-urls';
import auth from '@client/components/auth';
import axios from 'axios';

type PaginationOptions = {
  page: number;
  pageSize: number;
};

type Paginated<T> = {
  records: T[];
  meta: { totalItems: number; itemsOnPage: number; totalPages: number };
};

const appendPagination = (url: URL, pagination?: PaginationOptions) => {
  if (!pagination) {
    return;
  }

  url.searchParams.append('pagination[page]', pagination.page.toString());
  url.searchParams.append(
    'pagination[pageSize]',
    pagination.pageSize.toString(),
  );
};

const appendParams = (url: URL, params: URLSearchParams) =>
  params.forEach((value, key) => url.searchParams.append(key, value));

const fetchJson = <T>(url: URL): Promise<T> =>
  axios
    .get<T>(url.toString(), {
      headers: {
        Authorization: auth.getToken(),
      },
    })
    .then((res) => res.data);

const downloadSignedExport = async (
  influencerId: number,
  path: string,
  filters = new URLSearchParams(),
) => {
  const { data: signedToken } = await axios.get<string>(
    getCreatorApiUrl(`/crm/${influencerId}/export/sign-token`),
    {
      headers: {
        Authorization: auth.getToken(),
      },
    },
  );

  const url = new URL(getCreatorApiUrl(path));
  appendParams(url, filters);
  url.searchParams.append(
    'locale',
    Intl.DateTimeFormat().resolvedOptions().locale,
  );
  url.searchParams.append('utcOffset', String(new Date().getTimezoneOffset()));
  url.searchParams.append('signedToken', signedToken);

  const link = document.createElement('a');
  link.href = url.toString();
  // The signed token rides in the query string, so keep it out of any Referer.
  link.setAttribute('rel', 'noreferrer');
  // Valueless: cross-origin responses ignore it, so Content-Disposition wins.
  link.setAttribute('download', '');
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
};

type SubscribersFilters = {
  status: string[];
  classification: string[];
  product: string[];
  searchTerm?: string;
  label: string[];
};

const subscribersFilterParams = (filters: SubscribersFilters) => {
  const params = new URLSearchParams();

  if (filters.status.length >= 1)
    params.append('status', filters.status.join(','));
  if (filters.classification.length >= 1)
    params.append('classification', filters.classification.join(','));
  if (filters.product.length >= 1)
    params.append('product', filters.product.join(','));
  if (filters.label.length >= 1)
    params.append('label', filters.label.join(','));
  if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);

  return params;
};

type SubscribedResponse = Paginated<unknown>;
export function getSubscribedUsers(
  influencerId: number,
  options: SubscribersFilters & { pagination?: PaginationOptions },
): Promise<SubscribedResponse> {
  const url = new URL(getCreatorApiUrl(`/crm/${influencerId}/subscribers`));

  appendPagination(url, options.pagination);
  appendParams(url, subscribersFilterParams(options));

  return fetchJson<SubscribedResponse>(url);
}

export const downloadSubscribersExport = (
  influencerId: number,
  filters: SubscribersFilters,
) =>
  downloadSignedExport(
    influencerId,
    `/crm/${influencerId}/subscribers/export`,
    subscribersFilterParams(filters),
  );

type OtpFilters = {
  status: string[];
  otpOffers: string[];
  searchTerm?: string;
};

const otpFilterParams = (filters: OtpFilters) => {
  const params = new URLSearchParams();

  if (filters.status.length >= 1)
    params.append('status', filters.status.join(','));
  if (filters.otpOffers.length >= 1)
    params.append('products', filters.otpOffers.join(','));
  if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);

  return params;
};

type OtpResponse = Paginated<unknown>;
export function getOtpUsers(
  influencerId: number,
  options: OtpFilters & { pagination?: PaginationOptions },
): Promise<OtpResponse> {
  const url = new URL(
    getCreatorApiUrl(`/crm/${influencerId}/one-time-purchases`),
  );

  appendPagination(url, options.pagination);
  appendParams(url, otpFilterParams(options));

  return fetchJson<OtpResponse>(url);
}

export const downloadOtpExport = (influencerId: number, filters: OtpFilters) =>
  downloadSignedExport(
    influencerId,
    `/crm/${influencerId}/one-time-purchases/export`,
    otpFilterParams(filters),
  );

type FreeOffersFilters = {
  freeOffers: string[];
  convertedTo: string[];
  conversionStatus: string[];
  searchTerm?: string;
};

const freeOffersFilterParams = (filters: FreeOffersFilters) => {
  const params = new URLSearchParams();

  if (filters.freeOffers.length >= 1)
    params.append('freeOffers', filters.freeOffers.join(','));
  if (filters.convertedTo.length >= 1)
    params.append('convertedToProduct', filters.convertedTo.join(','));
  if (filters.conversionStatus.length >= 1)
    params.append('converted', filters.conversionStatus.join(','));
  if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);

  return params;
};

type FreeResponse = Paginated<unknown>;
export function getFreeOffersUsers(
  influencerId: number,
  options: FreeOffersFilters & { pagination?: PaginationOptions },
): Promise<FreeResponse> {
  const url = new URL(
    getCreatorApiUrl(`/crm/${influencerId}/free-one-time-purchases`),
  );

  appendPagination(url, options.pagination);
  appendParams(url, freeOffersFilterParams(options));

  return fetchJson<FreeResponse>(url);
}

export const downloadFreeOffersExport = (
  influencerId: number,
  filters: FreeOffersFilters,
) =>
  downloadSignedExport(
    influencerId,
    `/crm/${influencerId}/free-one-time-purchases/export`,
    freeOffersFilterParams(filters),
  );

type LeadsFilters = {
  source: string[];
  searchTerm?: string;
};

const leadsFilterParams = (filters: LeadsFilters) => {
  const params = new URLSearchParams();

  if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
  if (filters.source.length >= 1)
    params.append('source', filters.source.join(','));

  return params;
};

type LeadsResponse = Paginated<unknown>;
export function getLeadsUsers(
  influencerId: number,
  options: LeadsFilters & { pagination?: PaginationOptions },
): Promise<LeadsResponse> {
  const url = new URL(getCreatorApiUrl(`/crm/${influencerId}/leads`));

  appendPagination(url, options.pagination);
  appendParams(url, leadsFilterParams(options));

  return fetchJson<LeadsResponse>(url);
}

export const downloadLeadsExport = (
  influencerId: number,
  filters: LeadsFilters,
) =>
  downloadSignedExport(
    influencerId,
    `/crm/${influencerId}/leads/export`,
    leadsFilterParams(filters),
  );

type AbandonedCartsResponse = Paginated<unknown>;
export function getAbandonedCartsUsers(
  influencerId: number,
  options: {
    pagination?: PaginationOptions;
    searchTerm?: string;
  },
): Promise<AbandonedCartsResponse> {
  const url = new URL(getCreatorApiUrl(`/crm/${influencerId}/abandoned-carts`));

  appendPagination(url, options.pagination);

  if (options.searchTerm) {
    url.searchParams.append('searchTerm', options.searchTerm);
  }

  return fetchJson<AbandonedCartsResponse>(url);
}

type LeadsSource = {
  id: number;
  title: string;
  value: string;
  createdAt: string;
};
export function getLeadsSources(): Promise<LeadsSource[]> {
  return fetchJson<LeadsSource[]>(
    new URL(getCreatorApiUrl(`/crm/lead-sources`)),
  );
}

export const downloadExportAll = (influencerId: number) =>
  downloadSignedExport(influencerId, `/crm/${influencerId}/export/stream`);
